/**
 * useRecentsAndPins — tracks recently-used + pinned tool paths in localStorage.
 *
 *   • Recents:  last 8 distinct paths the user navigated to, newest first.
 *   • Pinned:   any number of user-pinned paths, manual order.
 *
 * Keys live under `cs.toolRecents` / `cs.toolPins` so they survive cache
 * wipes from the PWA auto-heal (we deliberately don't store these under
 * sessionStorage — pins should outlive a sign-out).
 */
import { useCallback, useEffect, useState } from 'react';

const RECENTS_KEY = 'cs.toolRecents';
const PINS_KEY = 'cs.toolPins';
const MAX_RECENTS = 8;

// Paths we never want to clutter recents with.
const IGNORED = new Set(['/', '/me', '/login', '/auth', '/auth/callback']);

const readArr = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(x => typeof x === 'string') : [];
  } catch { return []; }
};

const writeArr = (key, arr) => {
  try { localStorage.setItem(key, JSON.stringify(arr)); } catch { /* quota */ }
};

export default function useRecentsAndPins(currentPath) {
  const [recents, setRecents] = useState(() => readArr(RECENTS_KEY));
  const [pins, setPins] = useState(() => readArr(PINS_KEY));

  // Track navigation → push to recents (deduped, capped).
  useEffect(() => {
    if (!currentPath || IGNORED.has(currentPath)) return;
    setRecents(prev => {
      const next = [currentPath, ...prev.filter(p => p !== currentPath)].slice(0, MAX_RECENTS);
      writeArr(RECENTS_KEY, next);
      return next;
    });
  }, [currentPath]);

  // Cross-tab sync — react to other tabs writing pins.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === PINS_KEY) setPins(readArr(PINS_KEY));
      else if (e.key === RECENTS_KEY) setRecents(readArr(RECENTS_KEY));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const isPinned = useCallback((path) => pins.includes(path), [pins]);

  const togglePin = useCallback((path) => {
    setPins(prev => {
      const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
      writeArr(PINS_KEY, next);
      return next;
    });
  }, []);

  const clearRecents = useCallback(() => {
    writeArr(RECENTS_KEY, []);
    setRecents([]);
  }, []);

  return { recents, pins, isPinned, togglePin, clearRecents };
}
